import React, { useState, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  DataTable,
  Text,
  BlockStack,
  Box,
  Divider,
} from "@shopify/polaris";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import FilterBar from "../../Components/Analytics/FilterBar";
import analyticsApi from "../../Services/analyticsApi";

const COLORS = ["#5C6AC4", "#47C1BF", "#F49342", "#DC3545", "#00A6A0", "#9C6ADE"];

const Revenue = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    include_manual: false,
  });
  const [revenueData, setRevenueData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await analyticsApi.getRevenueSummary(filters);
      setRevenueData(response.data.data);
    } catch (error) {
      console.error("Error fetching revenue data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  if (isLoading) {
    return (
      <Page title="Revenue Analytics" backAction={{ content: "Back", url: "/admin/analytics" }}>
        <BlockStack gap="400">
          <Card>
            <Box padding="400">
              <Text>Loading revenue data...</Text>
            </Box>
          </Card>
        </BlockStack>
      </Page>
    );
  }

  if (!revenueData) {
    return (
      <Page title="Revenue Analytics" backAction={{ content: "Back", url: "/admin/analytics" }}>
        <Card>
          <Box padding="400">
            <Text>No revenue data available</Text>
          </Box>
        </Card>
      </Page>
    );
  }

  // Prepare product breakdown data for pie chart
  const productChartData = [
    {
      name: "Courses",
      value: revenueData.byProduct.courses.revenue,
      count: revenueData.byProduct.courses.count,
    },
    {
      name: "Lectures",
      value: revenueData.byProduct.lectures.revenue,
      count: revenueData.byProduct.lectures.count,
    },
    {
      name: "Notes",
      value: revenueData.byProduct.notes.revenue,
      count: revenueData.byProduct.notes.count,
    },
  ];

  // Prepare product breakdown data for table
  const productRows = [
    [
      "Courses",
      revenueData.byProduct.courses.count,
      `${revenueData.byProduct.courses.revenue.toFixed(2)} KWD`,
    ],
    [
      "Lectures",
      revenueData.byProduct.lectures.count,
      `${revenueData.byProduct.lectures.revenue.toFixed(2)} KWD`,
    ],
    [
      "Notes",
      revenueData.byProduct.notes.count,
      `${revenueData.byProduct.notes.revenue.toFixed(2)} KWD`,
    ],
  ];

  // Prepare payment method breakdown data for bar chart
  const paymentMethodChartData = revenueData.byPayment.by_payment_method.map((item) => ({
    name: item.payment_method || "Unknown",
    revenue: item.revenue,
    count: item.count,
  }));

  // Prepare payment method breakdown data for table
  const paymentMethodRows = revenueData.byPayment.by_payment_method.map((item) => [
    item.payment_method || "Unknown",
    item.count,
    `${item.revenue.toFixed(2)} KWD`,
  ]);

  // Prepare gateway breakdown data
  const gatewayChartData = revenueData.byPayment.by_gateway.map((item) => ({
    name: item.verified_from,
    revenue: item.revenue,
    count: item.count,
  }));

  const gatewayRows = revenueData.byPayment.by_gateway.map((item) => [
    item.verified_from,
    item.count,
    `${item.revenue.toFixed(2)} KWD`,
  ]);

  // Prepare trend data for line chart
  const trendChartData = revenueData.trend.map((item) => ({
    date: item.date,
    revenue: item.revenue,
    count: item.count,
  }));

  // Prepare trend data for table
  const trendRows = revenueData.trend.map((item) => [
    item.date,
    item.count,
    `${item.revenue.toFixed(2)} KWD`,
  ]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>{payload[0].name}</p>
          <p style={{ margin: "5px 0 0 0", color: COLORS[0] }}>
            Revenue: {payload[0].value?.toFixed(2)} KWD
          </p>
          {payload[0].payload.count && (
            <p style={{ margin: "5px 0 0 0" }}>Count: {payload[0].payload.count}</p>
          )}
        </div>
      );
    }
    return null;
  };

  const totalRevenue = (
    revenueData.byProduct.courses.revenue +
    revenueData.byProduct.lectures.revenue +
    revenueData.byProduct.notes.revenue
  ).toFixed(2);

  const totalSales =
    revenueData.byProduct.courses.count +
    revenueData.byProduct.lectures.count +
    revenueData.byProduct.notes.count;

  return (
    <Page
      title="Revenue Analytics"
      subtitle="Detailed revenue breakdown and trends"
      backAction={{ content: "Back", url: "/admin/analytics" }}
    >
      <BlockStack gap="500">
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          isLoading={isLoading}
        />

        {/* Summary Stats */}
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <Text variant="headingLg" as="h2">
                Summary
              </Text>
              <Divider />
              <Layout>
                <Layout.Section variant="oneThird">
                  <Box padding="200">
                    <BlockStack gap="100">
                      <Text variant="bodyMd" tone="subdued">
                        Total Revenue
                      </Text>
                      <Text variant="heading2xl" as="h3">
                        {totalRevenue} KWD
                      </Text>
                    </BlockStack>
                  </Box>
                </Layout.Section>
                <Layout.Section variant="oneThird">
                  <Box padding="200">
                    <BlockStack gap="100">
                      <Text variant="bodyMd" tone="subdued">
                        Total Sales
                      </Text>
                      <Text variant="heading2xl" as="h3">
                        {totalSales}
                      </Text>
                    </BlockStack>
                  </Box>
                </Layout.Section>
                <Layout.Section variant="oneThird">
                  <Box padding="200">
                    <BlockStack gap="100">
                      <Text variant="bodyMd" tone="subdued">
                        Average Order Value
                      </Text>
                      <Text variant="heading2xl" as="h3">
                        {totalSales > 0 ? (totalRevenue / totalSales).toFixed(2) : "0.00"} KWD
                      </Text>
                    </BlockStack>
                  </Box>
                </Layout.Section>
              </Layout>
            </BlockStack>
          </Box>
        </Card>

        {/* Revenue Trend Line Chart */}
        {trendChartData.length > 0 && (
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingLg" as="h2">
                  Revenue Trend Over Time
                </Text>
                <Divider />
                <div style={{ width: "100%", height: "400px" }}>
                  <ResponsiveContainer>
                    <LineChart data={trendChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        style={{ fontSize: "12px" }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis style={{ fontSize: "12px" }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke={COLORS[0]}
                        strokeWidth={3}
                        dot={{ fill: COLORS[0], r: 5 }}
                        name="Revenue (KWD)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric"]}
                  headings={["Date", "Sales Count", "Revenue"]}
                  rows={trendRows}
                  totals={[
                    "Total",
                    totalSales,
                    `${totalRevenue} KWD`,
                  ]}
                />
              </BlockStack>
            </Box>
          </Card>
        )}

        <Layout>
          {/* Product Breakdown Pie Chart */}
          <Layout.Section variant="oneHalf">
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="headingLg" as="h2">
                    Revenue by Product Type
                  </Text>
                  <Divider />
                  <div style={{ width: "100%", height: "400px" }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={productChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={(entry) =>
                            `${entry.name}: ${entry.value.toFixed(0)} KWD (${((entry.value / parseFloat(totalRevenue)) * 100).toFixed(1)}%)`
                          }
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {productChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <DataTable
                    columnContentTypes={["text", "numeric", "numeric"]}
                    headings={["Product Type", "Sales Count", "Revenue"]}
                    rows={productRows}
                    totals={[
                      "Total",
                      totalSales,
                      `${totalRevenue} KWD`,
                    ]}
                  />
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          {/* Gateway Breakdown */}
          <Layout.Section variant="oneHalf">
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="headingLg" as="h2">
                    Revenue by Gateway
                  </Text>
                  <Divider />
                  <div style={{ width: "100%", height: "400px" }}>
                    <ResponsiveContainer>
                      <BarChart data={gatewayChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" style={{ fontSize: "12px" }} />
                        <YAxis style={{ fontSize: "12px" }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="revenue" fill={COLORS[1]} name="Revenue (KWD)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <DataTable
                    columnContentTypes={["text", "numeric", "numeric"]}
                    headings={["Gateway", "Sales Count", "Revenue"]}
                    rows={gatewayRows}
                  />
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Payment Method Breakdown */}
        {paymentMethodRows.length > 0 && (
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingLg" as="h2">
                  Revenue by Payment Method
                </Text>
                <Divider />
                <div style={{ width: "100%", height: "400px" }}>
                  <ResponsiveContainer>
                    <BarChart data={paymentMethodChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" style={{ fontSize: "12px" }} />
                      <YAxis style={{ fontSize: "12px" }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="revenue" fill={COLORS[2]} name="Revenue (KWD)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric"]}
                  headings={["Payment Method", "Sales Count", "Revenue"]}
                  rows={paymentMethodRows}
                />
              </BlockStack>
            </Box>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
};

export default Revenue;
